import {
  AmericanFinalBracketSize,
  FixtureType,
  ForcedReason,
  TournamentFormat,
  type Fixture,
  type Group,
  type KnockoutNode,
  type Match,
  type Team,
  type Tournament,
} from '@/types/tournament.model';

/**
 * Returns the effective classified-per-group count to display for a tournament.
 * AMERICAN with NONE or FINAL bracket only classifies 1 team per group.
 */
export function getEffectiveClassifiedPerGroup(tournament: Tournament): number {
  if (
    tournament.format === TournamentFormat.AMERICAN &&
    (tournament.americanFinalBracketSize === AmericanFinalBracketSize.NONE ||
      tournament.americanFinalBracketSize === AmericanFinalBracketSize.FINAL)
  ) {
    return 1;
  }
  return tournament.classifiedPerGroups ?? 0;
}

/**
 * Whether the tournament is configured to end in a knockout bracket.
 * Mirrors the backend: in AMERICAN only FINAL/FOUR/EIGHT build a bracket —
 * NONE and null both mean "zones only". Any other format that reaches the
 * group stage always ends in knockouts.
 */
export function expectsKnockoutStage(
  tournament: Pick<Tournament, 'format' | 'americanFinalBracketSize'>,
): boolean {
  if (tournament.format !== TournamentFormat.AMERICAN) return true;
  const size = tournament.americanFinalBracketSize;
  return (
    size === AmericanFinalBracketSize.FINAL ||
    size === AmericanFinalBracketSize.FOUR ||
    size === AmericanFinalBracketSize.EIGHT
  );
}

/**
 * Returns the effective classified-per-group count for rendering group standings.
 * For SHORT format, an extra-classifier group of 4 promotes 3 instead of 2.
 * For AMERICAN the count comes pre-resolved (by americanFinalBracketSize); the
 * extra-classifier flag does not apply because there it encodes match format.
 */
export function computeGroupClassifiedCount(
  classifiedPerGroup: number,
  format: TournamentFormat | undefined,
  isExtraClassifierInGroupsOf4: boolean,
): number {
  const isAmerican = format === TournamentFormat.AMERICAN;
  if (isAmerican) {
    // In AMERICAN, `overflowGroupClassification` encodes the match FORMAT
    // (EXTRA_CLASSIFIER = "Por resultado"), not an extra classifier. The
    // classified count is already resolved by `americanFinalBracketSize`
    // (see getEffectiveClassifiedPerGroup), so no adjustment is applied.
    return classifiedPerGroup || 1;
  }
  return isExtraClassifierInGroupsOf4 ? 3 : classifiedPerGroup;
}

/**
 * Gets the Spanish label for a fixture type
 */
export const getFixtureTypeLabel = (fixtureType: FixtureType): string => {
  switch (fixtureType) {
    case FixtureType.GROUPS_AND_KNOCKOUTS:
      return 'Grupos + Eliminatorias';
    case FixtureType.KNOCKOUTS:
      return 'Solo Eliminatorias';
    default:
      return fixtureType;
  }
};

function collectBracketMatches(rootNode?: KnockoutNode): Match[] {
  const matches: Match[] = [];
  const walk = (node?: KnockoutNode): void => {
    if (!node) return;
    if (node.match) matches.push(node.match);
    walk(node.leftChild);
    walk(node.rightChild);
  };
  walk(rootNode);
  return matches;
}

function collectFixtureMatches(fixture: Fixture | null): Match[] {
  if (!fixture) return [];
  const fromGroups =
    fixture.groupList?.flatMap((group) => group.matchList ?? []) ?? [];
  return [
    ...fromGroups,
    ...collectBracketMatches(fixture.knockoutTree?.rootNode),
  ];
}

/** `0` -> `"Grupo A"`: groups have no name of their own, only their position in the fixture. */
export const groupLabel = (groupIndex: number): string =>
  `Grupo ${String.fromCharCode(65 + groupIndex)}`;

/** "1 partido" / "N partidos": the aggregate warning and the slot detail both count matches. */
export const matchCount = (n: number) =>
  n === 1 ? '1 partido' : `${n} partidos`;

export type ForcedMatchCounts = {
  unscheduled: number;
  overbooked: number;
};

/**
 * The backend recomputes `forcedReason` whenever a match is rescheduled, deleted, or created for
 * the next round, so these numbers come straight off the fixture payload with no extra request.
 * Editing the tournament's own time blocks does NOT trigger that recompute — adding a court leaves
 * an OVERBOOKED mark standing until one of its matches is moved.
 */
export function countForcedMatches(fixture: Fixture | null): ForcedMatchCounts {
  const matches = collectFixtureMatches(fixture);
  return {
    unscheduled: matches.filter(
      (m) => m.forcedReason === ForcedReason.UNSCHEDULED,
    ).length,
    overbooked: matches.filter(
      (m) => m.forcedReason === ForcedReason.OVERBOOKED,
    ).length,
  };
}

/**
 * Whether a single group is done: it has matches and every one of them already
 * carries a valid result. A group with no matches yet (LONG: none of its rounds
 * proposed/published) is NOT finished — `.every()` on an empty list is vacuously
 * true, and "every match created so far has a result" is not "the group is over"
 * while rounds are still being proposed one at a time.
 */
export function isGroupResolved(group: Group): boolean {
  const matches = group.matchList ?? [];
  if (matches.length === 0) return false;
  return matches.every(
    (match) => !!match.result && match.result.isValid === true,
  );
}

/**
 * Mirrors the backend swap rule: any loaded result (walkovers included, valid or
 * not) pins the team to its group.
 */
export function hasTeamPlayedInGroup(group: Group, teamId: string): boolean {
  return (group.matchList ?? []).some(
    (match) =>
      !!match.result &&
      (match.team1?.id === teamId || match.team2?.id === teamId),
  );
}

/** Where a team starts a knockout-only bracket: a first-round match, or a bye. */
export type BracketEntry = {
  teams: Team[];
  isBye: boolean;
  /**
   * Teams sharing it meet in their first match: the two of a first-round match,
   * or two byes feeding the same second-round match. Swapping them is a no-op.
   */
  matchupKey: string;
};

/** The bracket's leaves, top to bottom. */
export function getBracketEntries(rootNode: KnockoutNode): BracketEntry[] {
  const entries: BracketEntry[] = [];
  const isLeaf = (node?: KnockoutNode) =>
    !!node && !node.leftChild && !node.rightChild;
  const visit = (node: KnockoutNode, path: string) => {
    if (isLeaf(node)) {
      if (node.match) {
        const teams = [node.match.team1, node.match.team2].filter(
          (team): team is Team => !!team,
        );
        entries.push({ teams, isBye: false, matchupKey: path });
      } else if (node.advancingTeam) {
        entries.push({
          teams: [node.advancingTeam],
          isBye: true,
          matchupKey: path,
        });
      }
      return;
    }
    const { leftChild, rightChild } = node;
    const byesMeetHere =
      isLeaf(leftChild) &&
      isLeaf(rightChild) &&
      !leftChild?.match &&
      !rightChild?.match;
    if (leftChild) visit(leftChild, byesMeetHere ? path : `${path}L`);
    if (rightChild) visit(rightChild, byesMeetHere ? path : `${path}R`);
  };
  visit(rootNode, 'root');
  return entries;
}

/**
 * Mirrors the backend bracket swap rule: a loaded result anywhere in the
 * bracket (walkovers included) pins the team to its spot.
 */
export function getTeamsPlayedInBracket(rootNode: KnockoutNode): Set<string> {
  return new Set(
    collectBracketMatches(rootNode)
      .filter((match) => !!match.result)
      .flatMap((match) => [match.team1?.id, match.team2?.id])
      .filter((id): id is string => !!id),
  );
}

/**
 * Whether the group stage is over, i.e. every group is resolved.
 * A fixture with no groups counts as resolved: there is nothing left to play there.
 */
export function allGroupMatchesResolved(fixture: Fixture | null): boolean {
  return (fixture?.groupList ?? []).every(isGroupResolved);
}
